/**
 * React Select Input Wrapper
 * Styles overriding using this documentation guide https://react-select.com/styles
 */

import React from "react"
import PropTypes from "prop-types"
import theme from "styles/theme-main"
import {
  ControlWrapper,
  Label,
  LabelContainer,
  Hint,
  ErrorMessage,
  SubHeading,
  OptionalWrapper,
} from "./styles"
import Select, { components } from "react-select"
import { SideLabel } from "styles/Common"
import Text from "components/Text"
import OptionalLabel from "components/OptionalLabel"
import Translate from "components/Translate/container"

const MenuList = props => (
  <components.MenuList {...props} className={"MenuList"}></components.MenuList>
)

const SingleValue = props => (
  <components.SingleValue {...props}>
    {props.selectProps.overrideDisplayValue
      ? props.selectProps.overrideDisplayValue
      : props.data.selectedLabel || props.data.label}
  </components.SingleValue>
)

const Option = props => (
  <components.Option
    {...props}
    className={props.isSelected && "selectedOption"}
  >
    {props.data.label}
  </components.Option>
)

class SelectField extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      touched: false,
      menuOpen: false,
    }
  }

  componentWillUpdate(nextProps) {
    if (this.props.value && (!nextProps.value || nextProps.value === "")) {
      this.setState({ touched: false })
    }
  }

  handleChange = option => {
    const { id, onChange, callback } = this.props
    if (!onChange) {
      return
    }
    if (option == null || Array.isArray(option)) {
      onChange(id, option, callback)
      return
    }

    const { label, ...rest } = option
    onChange(id, { ...rest }, callback)
  }

  onMenuOpen = () => {
    const { onOpenScrollToOption } = this.props
    this.setState({ menuOpen: true }, () => {
      if (onOpenScrollToOption) {
        const menu = document.getElementsByClassName("MenuList")[0]
        const selectedOption = document.getElementsByClassName(
          "selectedOption"
        )[0]

        if (menu && selectedOption) {
          menu.scrollBy({ top: menu.offsetHeight / 2 })
        }
      }
    })
  }

  onMenuClose = () => {
    this.setState({ menuOpen: false })
  }

  render() {
    const {
      value,
      options,
      label,
      labelBold,
      optional,
      placeholderBold,
      labelSideText,
      hint,
      id,
      error,
      errorMessage,
      isClearable,
      size,
      disabled,
      forceErrorDisplay,
      subheading,
      hasRemoveButton,
      width,
      inline,
      variant,
      noMargin,
      boldOptions,
      noSelectState,
      subStage,
      displayErrorBottom,
      menuPlacement,
      overrideDisplayValue,
      translate = true,
      translateOptions = true,
      overflowModal = false,
    } = this.props

    const { menuOpen } = this.state

    const menuPortalTarget = overflowModal && document.body

    const customStyles = {
      menuPortal: base => ({ ...base, zIndex: 9999 }),
      container: (provided, state) => ({
        ...provided,
        width: "100%",
        maxWidth: "100%",
      }),
      placeholder: (provided, state) => ({
        ...provided,
        lineHeight: 1.3,
        fontSize: `${theme.selectField[variant].fontSize}`,
        marginLeft: `${theme.selectField[variant].marginLeft}`,
        fontWeight: placeholderBold
          ? "bold"
          : `${theme.selectField[variant].fontWeight}`,
        color: disabled
          ? theme.colors.disabled
          : `${theme.selectField[variant].color}`,
        textTransform: `${theme.selectField[variant].textTransform}`,
        letterSpacing: `${theme.selectField[variant].letterSpacing}`,
      }),
      menu: (provided, state) => ({
        ...provided,
        padding: "11px",
        fontWeight: boldOptions ? "bold" : "normal",
        borderRadius: "24px",
        boxShadow: "0 2px 10px 0 rgba(0, 0, 0, 0.2)",
        backgroundColor: "white",
      }),
      valueContainer: (provided, state) => ({
        ...provided,
        lineHeight: 1.3,
        padding: "9px 8px",
        minHeight: "46px",
      }),
      option: (provided, { isSelected, isFocused }) => ({
        ...provided,
        lineHeight: "1.3 !important",
        textAlign: "left",
        fontSize: "16px",
        paddingLeft: subStage ? "30px" : "10px",
        backgroundColor:
          isSelected && !noSelectState
            ? "#4a5b68 !important"
            : isFocused
            ? "#efefef !important"
            : null,
        color:
          isSelected && !noSelectState
            ? "#ffffff !important"
            : `${theme.selectField[variant].optionColor}`,
        cursor: "pointer",

        "&:hover": {
          backgroundColor: "#efefef",
        },
      }),

      input: (provided, state) => ({
        ...provided,
        fontSize: "16px",
        marginLeft: "13px",
        color: theme.colors.textPrimary,
        // eslint-disable-next-line no-useless-computed-key
        ["input"]: {
          lineHeight: 1.3,
        },
      }),
      singleValue: (provided, state) => ({
        ...provided,
        lineHeight: 1.3,
        fontSize: `${theme.selectField[variant].fontSize}`,
        marginLeft: "13px",
        fontWeight: "bold",
        color: disabled
          ? theme.colors.disabled
          : `${theme.selectField[variant].color}`,
        textTransform: `${theme.selectField[variant].textTransform}`,
        letterSpacing: `${theme.selectField[variant].letterSpacing}`,
      }),
      control: (provided, state) => ({
        ...provided,
        boxShadow: "none",
        borderRadius: "24px",
        fontWeight: "bold",
        minHeight: "48px",
        backgroundColor: `${theme.selectField[variant].background}`,
        borderColor:
          state.isFocused || state.isSelected
            ? theme.colors.primary
            : `${theme.selectField[variant].border}`,
        "&:hover": {
          borderColor: theme.colors.secondary,
        },
      }),
      indicatorSeparator: (provided, state) => ({
        ...provided,
        width: `${theme.selectField[variant].separatorWidth}`,
        backgroundColor: `${theme.selectField[variant].separatorColor}`,
        marginBottom: "3px",
        marginTop: "3px",
      }),
      dropdownIndicator: (provided, state) => ({
        ...provided,
        color:
          state.isFocused || state.isSelected
            ? `${theme.selectField[variant].iconColorDark}`
            : `${theme.selectField[variant].iconColor}`,
        transform: menuOpen ? "rotate(180deg)" : "none",
        transformOrigin: "center",
        transition: "transform 0.15s ease",
      }),
    }

    const groupStyles = {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: "14px",
      lineHeight: "1.43",
      letterSpacing: "0.2px",
      color: theme.colors.textSecondary,
      margin: "-3px 0 15px 0",
      textTransform: "none",
    }

    const formatGroupLabel = data => {
      return this.props.showSingle ? (
        <span />
      ) : (
        <div style={groupStyles}>
          <span>{data.label}</span>
        </div>
      )
    }

    const inlineStyles = {
      display: "inline-block",
      marginLeft: "12px",
    }

    const errorStyles = {
      container: (provided, state) => ({
        ...provided,
        width: "100%",
        maxWidth: "100%",
      }),
      placeholder: (provided, state) => ({
        ...provided,
        lineHeight: 1.3,
        fontSize: `${theme.selectField[variant].fontSize}`,
        marginLeft: `${theme.selectField[variant].marginLeft}`,
        fontWeight: placeholderBold
          ? "bold"
          : `${theme.selectField[variant].fontWeight}`,
        color: disabled
          ? theme.colors.disabled
          : `${theme.selectField[variant].color}`,
        textTransform: `${theme.selectField[variant].textTransform}`,
        letterSpacing: `${theme.selectField[variant].letterSpacing}`,
      }),
      menu: (provided, state) => ({
        ...provided,
        padding: "11px",
        fontWeight: boldOptions ? "bold" : "normal",
        borderRadius: "24px",
        boxShadow: "0 2px 10px 0 rgba(0, 0, 0, 0.2)",
        backgroundColor: "white",
      }),
      valueContainer: (provided, state) => ({
        ...provided,
        lineHeight: 1.3,
        padding: "9px 8px",
        minHeight: "46px",
      }),
      option: (provided, { isSelected, isFocused }) => ({
        ...provided,
        lineHeight: "1.3 !important",
        textAlign: "left",
        fontSize: "16px",
        paddingLeft: subStage ? "30px" : "10px",
        backgroundColor:
          isSelected && !noSelectState
            ? "#4a5b68 !important"
            : isFocused
            ? "#efefef !important"
            : null,
        color:
          isSelected && !noSelectState
            ? "#ffffff !important"
            : `${theme.selectField[variant].optionColor}`,
        cursor: "pointer",

        "&:hover": {
          backgroundColor: "#efefef",
        },
      }),

      input: (provided, state) => ({
        ...provided,
        fontSize: "16px",
        marginLeft: "13px",
        color: theme.colors.textPrimary,
        // eslint-disable-next-line no-useless-computed-key
        ["input"]: {
          lineHeight: 1.3,
        },
      }),
      singleValue: (provided, state) => ({
        ...provided,
        lineHeight: 1.3,
        fontSize: `${theme.selectField[variant].fontSize}`,
        marginLeft: "13px",
        fontWeight: "bold",
        color: disabled
          ? theme.colors.disabled
          : `${theme.selectField[variant].color}`,
        textTransform: `${theme.selectField[variant].textTransform}`,
        letterSpacing: `${theme.selectField[variant].letterSpacing}`,
      }),
      control: (provided, state) => ({
        ...provided,
        boxShadow: "none",
        borderRadius: "24px",
        fontWeight: "bold",
        minHeight: "48px",
        backgroundColor: `${theme.selectField[variant].background}`,
        borderColor: theme.colors.danger,
        "&:hover": {
          borderColor: theme.colors.secondary,
        },
      }),
      indicatorSeparator: (provided, state) => ({
        ...provided,
        width: `${theme.selectField[variant].separatorWidth}`,
        backgroundColor: `${theme.selectField[variant].separatorColor}`,
        marginBottom: "3px",
        marginTop: "3px",
      }),
      dropdownIndicator: (provided, state) => ({
        ...provided,
        color:
          state.isFocused || state.isSelected
            ? `${theme.selectField[variant].iconColorDark}`
            : `${theme.selectField[variant].iconColor}`,
        transform: menuOpen ? "rotate(180deg)" : "none",
        transformOrigin: "center",
        transition: "transform 0.15s ease",
      }),
    }

    const optionsWithLabel = options
      ? options.map(x => ({
          ...x,
          originalLabel: x.label || x.value,
          label:
            translate && translateOptions ? (
              <Translate>{x.label || x.value || x.name}</Translate>
            ) : (
              x.label || x.value || x.name
            ),
          value: x.value || x.label || x.name,
        }))
      : []

    const optionsFlatMapWithLabel = optionsWithLabel.flatMap(option =>
      option.options ? option.options : option
    )

    let valueWithLabel

    if (Array.isArray(value)) {
      valueWithLabel = optionsFlatMapWithLabel.filter(
        option =>
          !!value.find(x => x.value === option.value || x.id === option.id)
      )
    } else {
      valueWithLabel = optionsFlatMapWithLabel.find(
        option => option.value === value?.value || option.id === value?.id
      )
    }

    return (
      <div
        className={error && forceErrorDisplay ? "hasError" : null}
        style={inline ? inlineStyles : null}
      >
        {label ? (
          <LabelContainer hasHint={!!hint}>
            <Label
              as={"label"}
              labelBold={labelBold}
              htmlFor={id}
              subheading={subheading}
            >
              {translate ? <Translate>{label}</Translate> : label}
              {labelSideText && (
                <SideLabel>
                  <Translate>{labelSideText}</Translate>
                </SideLabel>
              )}
              {optional && (
                <OptionalWrapper>
                  <OptionalLabel />
                </OptionalWrapper>
              )}
            </Label>
            {subheading ? (
              <SubHeading as={"div"} variant={"hint"} inlineLabels>
                <Translate>{subheading}</Translate>
              </SubHeading>
            ) : null}
          </LabelContainer>
        ) : null}
        {hint ? (
          <Hint as={"div"} variant={"hint"}>
            {translate ? <Translate>{hint}</Translate> : hint}
          </Hint>
        ) : null}
        <ControlWrapper size={size} width={width} noMargin={noMargin}>
          <Select
            {...this.props}
            menuPortalTarget={menuPortalTarget}
            overrideDisplayValue={overrideDisplayValue}
            isDisabled={disabled}
            onMenuOpen={this.onMenuOpen}
            onMenuClose={this.onMenuClose}
            placeholder={
              <Translate>{this.props.placeholder || `Select`}</Translate>
            }
            isClearable={isClearable}
            size={size}
            value={valueWithLabel}
            onChange={value => {
              if (this.props.isMulti) {
                this.handleChange(
                  value?.map(({ id, value, originalLabel }) => {
                    const spread = typeof value !== "string" ? value : null
                    return { ...spread, id, value, label: originalLabel }
                  })
                )
              } else {
                this.handleChange({
                  ...value,
                  id: value?.id,
                  value: value?.value,
                  label: value?.originalLabel,
                })
              }
            }}
            options={optionsWithLabel}
            styles={error && forceErrorDisplay ? errorStyles : customStyles}
            formatGroupLabel={formatGroupLabel}
            components={{ MenuList, SingleValue, Option }}
            menuPlacement={menuPlacement}
            noOptionsMessage={this.props.noOptionsMessage}
          />
          {forceErrorDisplay && error && errorMessage ? (
            <ErrorMessage
              displayErrorBottom={displayErrorBottom}
              size={size}
              topMargin={errorMessage.length > 43 ? "0px" : "12px"}
              hasRemoveButton={hasRemoveButton}
            >
              <Text variant={"errorInputHelp"} id={`error-${id}`}>
                {translate ? (
                  <Translate>{errorMessage}</Translate>
                ) : (
                  errorMessage
                )}
              </Text>
            </ErrorMessage>
          ) : null}
        </ControlWrapper>
      </div>
    )
  }
}

SelectField.propTypes = {
  children: PropTypes.node,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  displayErrorBottom: PropTypes.bool,
  fullWidth: PropTypes.bool,
  noMargin: PropTypes.bool,
  errorMessage: PropTypes.node,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.node,
  labelBold: PropTypes.bool,
  placeholderBold: PropTypes.bool,
  medium: PropTypes.bool,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  validationType: PropTypes.oneOf(["onBlur", "onSubmit"]),
  size: PropTypes.oneOf([
    "small",
    "medium",
    "mediumLarge",
    "large",
    "fullWidth",
  ]),
  variant: PropTypes.oneOf([
    "standard",
    "primary",
    "control",
    "bold",
    "standardRolesScreeningVariant",
    "primarySubstage",
  ]),
  hasRemoveButton: PropTypes.string,
  width: PropTypes.string,
  noSelectState: PropTypes.bool,
}

SelectField.defaultProps = {
  noSelectState: false,
  required: false,
  select: false,
  fullWidth: true,
  noMargin: false,
  labelBold: false,
  placeholderBold: false,
  size: "large",
  variant: "standard",
  validationType: "onBlur",
  hasRemoveButton: "9px",
  width: "",
  separatorColor: "white",
  displayErrorBottom: false,
  onOpenScrollToOption: false,
}

export default SelectField
